/**
 * Agregación de ingredientes del plan semanal → líneas de lista de compra.
 *
 * Núcleo PURO (sin BD): recibe entradas del plan ya resueltas (receta +
 * ingredientes) y devuelve las líneas agregadas. Ver PRODUCT_PLAN.md
 * ("Agregación de ingredientes").
 */

import { normalizeIngredientName, normalizeUnit, toDisplayQuantity, unitFamilyKey } from "./units";

export interface PlannedIngredient {
  /** Nombre libre del ingrediente ("Tomate frito"). */
  name: string;
  /** Cantidad para las porciones BASE de la receta; null = "al gusto". */
  quantity: number | null;
  unit: string | null;
  /** Nombre del pasillo/categoría asignado al ingrediente (denormalizado). */
  category?: string | null;
  /** Porciones base de la receta a la que pertenece. */
  recipeServings: number;
  /** Porciones planificadas en la entrada del plan (null = las de la receta). */
  plannedServings?: number | null;
}

export interface PantryStock {
  name: string;
  quantity: number | null;
  unit: string | null;
}

export interface AggregatedItem {
  /** Nombre a mostrar (primera aparición, con su capitalización original). */
  name: string;
  /** Cantidad ya convertida a la mejor unidad de presentación; null = sin cantidad. */
  quantity: number | null;
  unit: string | null;
  category: string | null;
  /** Clave estable nombre+familia para conservar checks al regenerar. */
  key: string;
}

interface Bucket {
  displayName: string;
  family: ReturnType<typeof normalizeUnit>;
  total: number;
  hasQuantity: boolean;
  category: string | null;
  key: string;
}

/** Factor de escalado de una entrada del plan. */
export function servingsFactor(recipeServings: number, plannedServings?: number | null): number {
  if (!plannedServings || recipeServings <= 0) {
    return 1;
  }
  return plannedServings / recipeServings;
}

/** Clave estable de agregación: nombre normalizado + familia de unidad. */
export function aggregationKey(name: string, unit: string | null | undefined): string {
  const normalized = normalizeUnit(unit);
  return `${normalizeIngredientName(name)}|${unitFamilyKey(normalized)}`;
}

/**
 * Agrega los ingredientes planificados:
 *  1. escala por porciones (plannedServings / recipeServings),
 *  2. convierte a la unidad base de su familia y suma por (nombre, familia),
 *  3. resta la despensa (si se pasa) con clamp a 0 — los totalmente cubiertos
 *     desaparecen,
 *  4. presenta cada línea en su mejor unidad (g→kg, ml→l).
 */
export function aggregateIngredients(
  ingredients: PlannedIngredient[],
  pantry: PantryStock[] = [],
): AggregatedItem[] {
  const buckets = new Map<string, Bucket>();

  for (const ing of ingredients) {
    const trimmedName = ing.name.trim();
    if (!trimmedName) {
      continue;
    }
    const normalized = normalizeUnit(ing.unit);
    const key = `${normalizeIngredientName(trimmedName)}|${unitFamilyKey(normalized)}`;

    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = {
        displayName: trimmedName,
        family: normalized,
        total: 0,
        hasQuantity: false,
        category: ing.category?.trim() || null,
        key,
      };
      buckets.set(key, bucket);
    }
    if (!bucket.category && ing.category?.trim()) {
      bucket.category = ing.category.trim();
    }

    if (ing.quantity !== null && ing.quantity !== undefined) {
      const factor = servingsFactor(ing.recipeServings, ing.plannedServings);
      bucket.total += ing.quantity * factor * normalized.factor;
      bucket.hasQuantity = true;
    }
  }

  // Despensa: restar stock por la misma clave (nombre + familia).
  for (const stock of pantry) {
    const normalized = normalizeUnit(stock.unit);
    const key = `${normalizeIngredientName(stock.name)}|${unitFamilyKey(normalized)}`;
    const bucket = buckets.get(key);
    if (!bucket) {
      continue;
    }
    if (stock.quantity === null || stock.quantity === undefined) {
      // Stock sin cantidad = "lo tengo" → cubre por completo el ingrediente.
      buckets.delete(key);
      continue;
    }
    if (bucket.hasQuantity) {
      bucket.total -= stock.quantity * normalized.factor;
      if (bucket.total <= 0) {
        buckets.delete(key);
      }
    }
  }

  const items: AggregatedItem[] = [];
  for (const bucket of buckets.values()) {
    if (!bucket.hasQuantity) {
      items.push({
        name: bucket.displayName,
        quantity: null,
        unit: null,
        category: bucket.category,
        key: bucket.key,
      });
      continue;
    }
    const display = toDisplayQuantity(bucket.total, bucket.family.family, bucket.family.raw);
    items.push({
      name: bucket.displayName,
      quantity: display.quantity,
      unit: display.unit,
      category: bucket.category,
      key: bucket.key,
    });
  }

  // Orden estable: categoría (alfabética, sin categoría al final) + nombre.
  items.sort((a, b) => {
    const catA = a.category ?? "￿";
    const catB = b.category ?? "￿";
    if (catA !== catB) {
      return catA.localeCompare(catB);
    }
    return a.name.localeCompare(b.name);
  });

  return items;
}
